<template>
  <div class="flex-column d-flex">
    <v-card rounded="0" width="100%" elevation="0" class="quiz-card">
      <Typography as="p" variant="h-5" class="quiz-question__title mb-0">
        {{ question.content }}{{ !isRequired ? " (optional)" : "" }}
      </Typography>
      <Typography as="p" variant="body-2" class="pt-5 quiz-question__helper mb-0">
        Choose a response from the answers below
      </Typography>
      <v-divider class="mt-5" />

      <v-radio-group
        v-model="checkedAnswerId"
        :class="answerSubmitted ? 'radio-group-disabled' : ''"
      >
        <div v-for="answer in question.LearningQuizAnswers" :key="answer.learningQuizAnswerId">
          <v-hover v-slot="{ hover }">
            <div
              class="answer-box d-flex align-center cursor-pointer my-1"
              :class="{
                'light-grey-background': hover && !answerSubmitted,
                'light-red-background':
                  !isSurveyQuestion &&
                  answerSubmitted &&
                  answer.correctness <= 0 &&
                  answer.learningQuizAnswerId == checkedAnswerId,
                'brand-pale-green-background':
                  !isSurveyQuestion && answerSubmitted && answer.correctness > 0,
              }"
              @click="checkedAnswerId = answer.learningQuizAnswerId"
            >
              <!-- Show check icon only for quizzes with correct answers -->
              <div
                class="d-flex justify-center"
                v-if="!isSurveyQuestion && answerSubmitted && answer.correctness > 0"
              >
                <v-icon color="brandGreen" class="correct-icon">mdi-check-circle</v-icon>
                <span class="ml-2">{{ answer.content }}</span>
              </div>
              <v-radio
                v-else
                :label="answer.content"
                :value="answer.learningQuizAnswerId"
                :color="answerSubmitted ? 'grey' : 'brandPrimary'"
                :readonly="answerSubmitted"
                :disabled="answerSubmitted"
              ></v-radio>
            </div>
          </v-hover>
        </div>
      </v-radio-group>
      <v-divider />

      <div class="d-flex justify-center mt-10">
        <v-btn
          v-if="answerSubmitted == false"
          :color="!inProgram ? 'brandPrimary' : 'brand'"
          :rounded="!inProgram"
          class="white--text"
          width="120"
          depressed
          :loading="postingResponse"
          :disabled="isMissingRequiredValue(checkedAnswerId)"
          @click="checkQuestion"
        >
          {{ computedSubmitButton }}
        </v-btn>

        <v-btn
          :color="!inProgram ? 'brandPrimary' : 'brand'"
          :rounded="!inProgram"
          class="white--text"
          @click="goToNextQuestion"
          width="120"
          depressed
          v-if="!isLastQuestion && answerSubmitted == true"
        >
          Next
        </v-btn>

        <v-btn
          :color="!inProgram ? 'brandPrimary' : 'brand'"
          :rounded="!inProgram"
          class="white--text"
          @click="completeQuiz"
          width="120"
          depressed
          v-else-if="isLastQuestion && answerSubmitted == true && !isSurveyQuestion"
        >
          Complete
        </v-btn>
        <!-- </v-col> -->
      </div>
    </v-card>
  </div>
</template>

<script>
import Typography from "@/components/generic/Typography.vue";
import quizQuestionResponseMixin from "@/components/learning/QuizQuestion/mixins/quizQuestionResponseMixin";

export default {
  name: "QuizQuestionMultipleChoiceV2",
  mixins: [quizQuestionResponseMixin],
  components: { Typography },
  data() {
    return {
      correctCount: 0,
      checkedAnswerId: null,
      answerSubmitted: false,
    };
  },
  methods: {
    clickAnswer(x) {
      if (this.answerSubmitted) return;
      this.checkedAnswerId = x;
    },
    checkQuestion() {
      if (
        this.skipOptionalUnanswered(this.checkedAnswerId, {
          onSuccess: () => {
            this.correctCount++;
            this.handleSuccess();
          },
        })
      ) {
        return;
      }

      var correctAnswers =
        this.question?.LearningQuizAnswers?.filter((ans) => ans.correctness > 0)?.map(
          (x) => x.learningQuizAnswerId
        ) || [];

      if (
        correctAnswers.includes(this.checkedAnswerId) ||
        correctAnswers.length == 0 ||
        !this.isRequired
      ) {
        this.correctCount++;
      }

      const quizQuestionResponse = this.buildBaseResponse({
        learningQuizAnswerId: "[" + this.checkedAnswerId + "]",
        value: this.checkedAnswer.content,
        correctness: this.isSurveyQuestion ? null : this.checkedAnswer.correctness,
      });

      this.postResponseAndAdvance([quizQuestionResponse], {
        onSuccess: this.handleSuccess,
        onError: this.handleError,
      });
    },
    handleSuccess() {
      this.answerSubmitted = true;
      if (
        this.isSurveyQuestion ||
        this.noCorrectAnswersMarked ||
        (!this.isRequired && !this.hasValue(this.checkedAnswerId))
      ) {
        this.goToNextQuestion();
      }
    },
    handleError() {
      this.answerSubmitted = false;
    },
    goToNextQuestion() {
      if (!this.isLastQuestion) {
        this.answerSubmitted = false;
        this.checkedAnswerId = null;
        this.$emit("next-question", this.correctCount);
        this.correctCount = 0;
      } else {
        this.completeQuiz();
      }
    },
    completeQuiz() {
      this.$emit("complete-quiz", this.correctCount);
    },
  },
  computed: {
    checkedAnswer() {
      if (this.checkedAnswerId == null) return null;
      return this.question.LearningQuizAnswers.find(
        (x) => x.learningQuizAnswerId == this.checkedAnswerId
      );
    },
  },
};
</script>

<style scoped>
/* Figma: question = Headline/h5 (20px), helper = Body/Body 2 (14px, 0.6), labels = Body/Body 1 (16px) */
.quiz-question__title {
  color: rgba(0, 0, 0, 0.87);
  word-break: normal;
}

.quiz-question__helper {
  color: rgba(0, 0, 0, 0.6);
}

.quiz-card >>> .v-label {
  font-family: "Avenir Book", Helvetica, Arial, sans-serif !important;
  font-size: 16px !important;
  line-height: 1.5em !important;
  color: rgba(0, 0, 0, 0.87) !important;
}

.light-grey-background {
  background-color: var(--v-lightGrey-base);
}

.light-red-background {
  background-color: #fcdeda;
}

.brand-pale-green-background {
  background-color: var(--v-brandPaleGreen-base);
}

.answer-box {
  min-height: 60px;
  width: 100%;
  padding: 0 20px 0 20px;
}

.correct-icon {
  transform: scale(1.1);
}

.radio-group-disabled {
  pointer-events: none !important;
}
</style>
